// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The camera-following notice panel, in the browser (M9-47, 06 §6, 06 §7, NFR-022).
//
// **The browser could not say anything to a presenting player.** Every sentence it produces in an
// immersive session goes to the DOM — `XRFrameReport.toast` is read once, in `MachineStage.tsx`,
// and rendered as HTML — and in an immersive WebXR session the DOM is not composited. So the
// seated-mode offer, the recycle countdown, the room-fit note, *"Look where you want the machine
// and pinch"*, the anchor-lost line and the input-scheme change were all invisible to the only
// person they were for. This is also **NFR-022's second half** — *"captioned on screen **and in the
// headset**"* — which 06 §6 names as *"a screen-fixed panel in the headset"*, and which the native
// client has drawn since M7-22.
//
// **The same panel the native client draws, at the same distances.** `RecycleNotice.cs` hangs a
// 0.62 m quad 1.15 m ahead and 0.42 m below the eyeline, catching up with the head at two thirds of
// the gap a second. Every one of those numbers is repeated here rather than re-chosen, because two
// clients that put the same sentence in different places are two answers to *where does this
// product speak from*.
//
// **It follows softly and is never welded.** A panel re-planted on the head every frame is, in this
// repository's own words about the toast that did it, *"the classic way to make somebody ill"*. The
// ease is per-second and frame-rate independent, so a 72 Hz headset and a 90 Hz one settle at the
// same speed rather than the 90 Hz one following harder.
//
// **Below the eyeline, not over the machine.** 06 §7 is explicit that this is not a modal: it goes
// where the player is already looking and never takes the controls. 0.42 m below the eye at 1.15 m
// puts it under a cabinet's glass rather than across it.

import {
  CanvasTexture,
  DoubleSide,
  Group,
  LinearFilter,
  Mesh,
  MeshBasicMaterial,
  PlaneGeometry,
  SRGBColorSpace,
  type BufferGeometry,
  type Material,
} from 'three'
import {
  NOTICE_HEIGHT,
  NOTICE_WIDTH,
  noticePixels,
} from '../xr/sessionNotice'

/** How far in front of the player it hangs, metres. `RecycleNotice.Distance`. */
export const NOTICE_DISTANCE = 1.15

/** How far below the eyeline, metres. Under a machine's glass, not over it. */
export const NOTICE_BELOW_EYE = 0.42

/** Panel width, metres. The height follows from the dot raster's 8:1 shape. */
export const NOTICE_PANEL_WIDTH = 0.62

/**
 * How quickly it catches up with the head, per second.
 *
 * Two thirds of the gap a second: quick enough that a turn does not leave it behind, slow enough
 * that it is plainly a thing in the room rather than a thing on the lens.
 */
export const NOTICE_FOLLOW = 0.66

/** A head pose, reduced to what placing a panel needs. */
export interface NoticeHead {
  readonly x: number
  readonly y: number
  readonly z: number
  /** Forward, as a unit vector. Levelled here, so a player looking at their feet is handled. */
  readonly fx: number
  readonly fy: number
  readonly fz: number
}

export interface NoticePanelBuild {
  readonly group: Group
  readonly geometries: readonly BufferGeometry[]
  readonly materials: readonly Material[]
  readonly textures: readonly CanvasTexture[]
  /**
   * Say this, or nothing. Cheap to call every frame.
   *
   * Redrawn only when the sentence changes, like the wrist panel: a texture upload per frame for
   * text that changes a few times a session is the difference between free and megabytes a second
   * on a device that has neither to spare.
   */
  update: (text: string | undefined, head: NoticeHead | undefined, frameSeconds: number) => void
  dispose: () => void
}

/**
 * Where the panel wants to be, given a head.
 *
 * Pure and exported so the placement can be asserted without a renderer — the same split the rest
 * of `src/xr` uses, and the reason `sessionNotice.ts` holds the raster and this holds the quad.
 *
 * **Levelled forward, and a fallback when there is none.** A panel placed along a vertical forward
 * vector ends up on top of the player, which is what happens when somebody looks at their own feet
 * or at the ceiling — so the forward is flattened and, when nothing is left of it, the panel goes
 * straight ahead in the room's own frame.
 */
export function noticeTarget(head: NoticeHead): { x: number; y: number; z: number } {
  let fx = head.fx
  let fz = head.fz
  const flat = Math.hypot(fx, fz)
  if (flat < 1e-2) {
    fx = 0
    fz = -1
  } else {
    fx /= flat
    fz /= flat
  }
  return {
    x: head.x + fx * NOTICE_DISTANCE,
    y: head.y - NOTICE_BELOW_EYE,
    z: head.z + fz * NOTICE_DISTANCE,
  }
}

/**
 * How far to move toward the target this frame, as a fraction of the gap.
 *
 * `1 − (1 − follow)^seconds`, which is the frame-rate-independent form of "two thirds of the gap a
 * second" — the same expression `RecycleNotice.Follow_` uses. A per-frame lerp would settle twice
 * as fast at 144 Hz as at 72, which is a panel that behaves differently on different hardware for
 * no reason a player could name.
 */
export function noticeEase(frameSeconds: number): number {
  return 1 - Math.pow(1 - NOTICE_FOLLOW, frameSeconds)
}

export function buildNoticePanel(): NoticePanelBuild {
  const group = new Group()
  group.name = 'session notice'
  group.visible = false

  const host = (globalThis as { document?: { createElement: (tag: string) => HTMLCanvasElement } })
    .document
  const canvas = host?.createElement('canvas')
  const ctx = canvas?.getContext('2d') ?? null
  if (canvas !== undefined) {
    canvas.width = NOTICE_WIDTH
    canvas.height = NOTICE_HEIGHT
  }

  const texture = canvas === undefined ? undefined : new CanvasTexture(canvas)
  if (texture !== undefined) {
    texture.colorSpace = SRGBColorSpace
    texture.generateMipmaps = false
    texture.minFilter = LinearFilter
  }

  const height = (NOTICE_PANEL_WIDTH * NOTICE_HEIGHT) / NOTICE_WIDTH
  const geometry = new PlaneGeometry(NOTICE_PANEL_WIDTH, height)
  const material = new MeshBasicMaterial({
    ...(texture === undefined ? { color: 0xffa032 } : { map: texture }),
    transparent: true,
    side: DoubleSide,
    // **Unlit and tone-mapped out.** M7-40 is the whole reason this is spelt out: a passthrough
    // room flattened `#e8ecff` text and an `rgba(10,12,22)` ground to the same colour, because
    // every generated panel was being drawn lit while its own contrast was chosen unlit.
    toneMapped: false,
    // Over the machine rather than through it. A warning occluded by the cabinet it is warning
    // about is a warning that did not appear.
    depthTest: false,
  })
  const mesh = new Mesh(geometry, material)
  mesh.renderOrder = 998
  group.add(mesh)

  let showing: string | undefined
  let placed = false

  const draw = (text: string): void => {
    if (ctx === null || texture === undefined) return
    const pixels = noticePixels(text)
    // `putImageData` rather than a fill and a stroke: the raster is already the picture, and
    // going through the 2-D context's text engine would be a second typeface in a product whose
    // content rule is that every glyph is generated.
    ctx.putImageData(new ImageData(pixels, NOTICE_WIDTH, NOTICE_HEIGHT), 0, 0)
    texture.needsUpdate = true
  }

  const update = (
    text: string | undefined,
    head: NoticeHead | undefined,
    frameSeconds: number,
  ): void => {
    if (text === undefined || text.length === 0) {
      group.visible = false
      // **Forgotten, so the next notice eases in from where the player is rather than from where
      // the last one was.** A panel that kept its position across a gap of minutes would fly
      // across the room on the frame the next sentence arrived.
      placed = false
      showing = undefined
      return
    }
    if (text !== showing) {
      showing = text
      draw(text)
    }
    group.visible = true
    if (head === undefined) return

    const want = noticeTarget(head)
    if (!placed) {
      group.position.set(want.x, want.y, want.z)
      placed = true
    } else {
      const ease = noticeEase(frameSeconds)
      group.position.x += (want.x - group.position.x) * ease
      group.position.y += (want.y - group.position.y) * ease
      group.position.z += (want.z - group.position.z) * ease
    }
    // Facing the eye rather than facing along the forward vector: the two differ once the panel
    // is lagging behind a turn, and the one a reader wants is the one squared to them.
    group.lookAt(head.x, head.y, head.z)
  }

  return {
    group,
    geometries: [geometry],
    materials: [material],
    textures: texture === undefined ? [] : [texture],
    update,
    dispose: () => {
      geometry.dispose()
      material.dispose()
      texture?.dispose()
    },
  }
}
